import type {
  CorvidSDKPropsFactory,
  CorvidSDKFactory,
} from '@wix/editor-elements-types/corvid';
import { withValidation } from '../validations';
import { reportError } from '../reporters';
import { messages } from '../messages';
import type {
  IForegroundColorSDK,
  IBorderRadiusSDK,
  IBorderWidthSDK,
  IStylePropsSDKOptions,
  IFillColorSDK,
  IStrokeColorSDK,
  ITextColorWithStatesSDK,
  IBackgroundColorWithStateSDK,
  IBorderColorWithStatesSDK,
  IIconColorWithStatesSDK,
  StyleState,
} from './styleSDKFactories';
import {
  createBackgroundColorPropsSDKFactory,
  createBorderColorPropsSDKFactory,
  createBorderRadiusPropsSDKFactory,
  createBorderWidthPropsSDKFactory,
  createForegroundColorPropsSDKFactory,
  createTextColorPropsSDKFactory,
  createFillColorPropsSDKFactory,
  createStrokeColorPropsSDKFactory,
  createIconColorPropsSDKFactory,
} from './styleSDKFactories';
import { composeFactory } from './composeFactoryWithReset';
import { styleStates } from './styleSDKFactories/constants';

type StyleProps = ITextColorWithStatesSDK &
  IBackgroundColorWithStateSDK &
  IBorderColorWithStatesSDK &
  IBorderRadiusSDK &
  IBorderWidthSDK &
  IFillColorSDK &
  IStrokeColorSDK &
  IForegroundColorSDK &
  IIconColorWithStatesSDK;

export interface IStylePropSDK {
  style: Partial<StyleProps> & {
    removeProperty: (styleProp: keyof StyleProps) => void;
  };
}

const STYLE_SDK_RESET_METHOD_NAME = 'reset' as const;

type SDKWithReset = {
  [STYLE_SDK_RESET_METHOD_NAME]: (prop: string) => void;
};

function composeSDKFactoriesWithReset<
  TProps extends object,
  TSDK extends object,
  TData extends object = object,
>(
  ...sources: Array<CorvidSDKFactory<any, any, any>>
): CorvidSDKFactory<TProps, TSDK & SDKWithReset, TData> {
  const compose = composeFactory(STYLE_SDK_RESET_METHOD_NAME);
  return api => {
    const sdks = sources.map(source => source(api));

    const styleStateComposedSdks: Partial<Record<StyleState, object>> = {};
    for (const styleState of styleStates) {
      if (!sdks.some(sdk => styleState in sdk)) {
        continue;
      }

      styleStateComposedSdks[styleState] = compose(
        ...sdks.map(sdk => sdk[styleState] ?? {}),
      );

      // we "hide" the reset method as it's not a public API
      Object.defineProperty(
        styleStateComposedSdks[styleState],
        STYLE_SDK_RESET_METHOD_NAME,
        { enumerable: false },
      );

      sdks.forEach(sdk => {
        // we remove style state properties from the main sdk to prevent overrides after compose of all sdks
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete sdk[styleState];
      });
    }

    const composedSdk = compose(...sdks) as any as TSDK & SDKWithReset;

    // we "hide" the reset method as it's not a public API
    Object.defineProperty(composedSdk, STYLE_SDK_RESET_METHOD_NAME, {
      enumerable: false,
    });

    return Object.assign(composedSdk, styleStateComposedSdks);
  };
}

export type StyleSDKOptions = {
  cssVarPrefix?: string;
};

const _stylePropsSDKFactory =
  (
    supportedSDKFactories: CorvidSDKFactory<object, object, any, any>,
  ): CorvidSDKPropsFactory<any, IStylePropSDK> =>
  api => {
    const styleSDKs = supportedSDKFactories(api) as IStylePropSDK['style'] &
      SDKWithReset;

    styleSDKs.removeProperty = (propertyName: keyof StyleProps) => {
      const [propName, styleState] = propertyName.includes('.')
        ? (propertyName.split('.').reverse() as [keyof StyleProps, StyleState])
        : [propertyName, undefined];

      if (
        (!styleState && !(propName in styleSDKs)) ||
        (styleState && !(propName in (styleSDKs[styleState] ?? {})))
      ) {
        const styleSdkPropNames = Object.keys(styleSDKs).filter(
          k => k !== 'removeProperty',
        );
        reportError(
          messages.invalidEnumValueMessage({
            functionName: 'removeProperty',
            propertyName: 'propertyName',
            value: propertyName,
            enum: styleSdkPropNames,
            index: undefined,
          }),
        );
        return;
      }

      const sdk = styleState
        ? (styleSDKs[styleState] as unknown as SDKWithReset)
        : styleSDKs;

      sdk[STYLE_SDK_RESET_METHOD_NAME]?.(propName);
    };

    return {
      get style() {
        return styleSDKs;
      },
    };
  };

type IStyleCSSProperty =
  | 'BackgroundColor'
  | 'BorderColor'
  | 'BorderWidth'
  | 'ForegroundColor'
  | 'BorderRadius'
  | 'TextColor'
  | 'FillColor'
  | 'StrokeColor'
  | 'IconColor';

const styleFactories = {
  BackgroundColor: createBackgroundColorPropsSDKFactory,
  BorderColor: createBorderColorPropsSDKFactory,
  BorderWidth: createBorderWidthPropsSDKFactory,
  ForegroundColor: createForegroundColorPropsSDKFactory,
  BorderRadius: createBorderRadiusPropsSDKFactory,
  TextColor: createTextColorPropsSDKFactory,
  FillColor: createFillColorPropsSDKFactory,
  StrokeColor: createStrokeColorPropsSDKFactory,
  IconColor: createIconColorPropsSDKFactory,
} as const;

const styleFactoriesDefaultOptions: Record<
  IStyleCSSProperty,
  IStylePropsSDKOptions
> = {
  BackgroundColor: {
    supportOpacity: true,
  },
  BorderColor: {
    supportOpacity: true,
  },
  BorderWidth: {},
  ForegroundColor: {
    supportOpacity: true,
  },
  BorderRadius: {},
  TextColor: {},
  FillColor: {
    supportOpacity: true,
  },
  StrokeColor: {
    supportOpacity: true,
  },
  IconColor: {},
};

export const createStylePropsSDKFactory = (
  list: Partial<Record<IStyleCSSProperty, boolean | IStylePropsSDKOptions>>,
  styleSDKOptions?: StyleSDKOptions,
) => {
  const supported = (Object.keys(list) as Array<IStyleCSSProperty>).filter(
    value => list[value],
  );

  const supportedSDKFactories = supported.map(value => {
    const stylePropertyOptions =
      typeof list[value] !== 'boolean'
        ? (list[value] as IStylePropsSDKOptions)
        : styleFactoriesDefaultOptions[value];

    return styleFactories[value]({
      prefix: styleSDKOptions?.cssVarPrefix,
      withoutDefaultValue: stylePropertyOptions.withoutDefaultValue,
      supportOpacity: stylePropertyOptions.supportOpacity,
      supportedStates: stylePropertyOptions.supportedStates,
    });
  });

  return withValidation(
    _stylePropsSDKFactory(
      composeSDKFactoriesWithReset(...supportedSDKFactories),
    ),
    {
      type: ['object'],
      properties: {
        style: {
          type: ['object'],
        },
      },
    },
  );
};
